import React from 'react';
import './Legal.css';

export default function Terms() {
  return (
    <div className="legal-page">
      <section className="page-hero">
        <div className="container">
          <span className="section-label">Legal</span>
          <h1 className="section-title">Terms of Service</h1>
          <p className="section-subtitle">Last updated: May 1, 2026</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="legal-content">
            <p className="legal-intro">
              Welcome to AIScaler. By accessing or using our platform at aiscaler.app ("Service"), you agree to be bound by these Terms of Service ("Terms"). Please read them carefully.
            </p>

            <h2>1. Acceptance of Terms</h2>
            <p>By creating an account or using AIScaler, you agree to these Terms and our Privacy Policy. If you do not agree, you may not use the Service. If you are using the Service on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.</p>

            <h2>2. Description of Service</h2>
            <p>AIScaler provides AI-powered media enhancement services including:</p>
            <ul>
              <li>Image upscaling and enhancement (2x, 4x, 8x)</li>
              <li>Video upscaling, frame interpolation, and stabilization</li>
              <li>Document and text enhancement</li>
              <li>Batch processing and workflow automation</li>
              <li>REST API and developer SDKs</li>
              <li>Platform integrations (Shopify, Canva, Figma, etc.)</li>
            </ul>

            <h2>3. Account Registration</h2>
            <p>To use certain features, you must create an account. You agree to:</p>
            <ul>
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Be responsible for all activity under your account</li>
            </ul>

            <h2>4. Acceptable Use</h2>
            <p>You agree not to use AIScaler to:</p>
            <ul>
              <li>Upload content that is illegal, harmful, or violates third-party rights</li>
              <li>Generate deepfakes or manipulated media intended to deceive</li>
              <li>Process content that contains child exploitation material</li>
              <li>Attempt to reverse-engineer our AI models or algorithms</li>
              <li>Abuse the API with excessive requests beyond your plan limits</li>
              <li>Resell the Service without written authorization</li>
              <li>Interfere with the platform's infrastructure or security</li>
            </ul>

            <h2>5. Intellectual Property</h2>
            <h3>5.1 Your Content</h3>
            <p>You retain full ownership of all content you upload to AIScaler. By uploading content, you grant us a limited, temporary license to process that content solely for the purpose of providing the enhancement service. We do not claim any ownership of your content.</p>

            <h3>5.2 Our Platform</h3>
            <p>AIScaler, including its technology, design, branding, and AI models, is owned by us and protected by intellectual property laws. You may not copy, modify, distribute, or create derivative works of our platform without written permission.</p>

            <h2>6. Subscription Plans & Billing</h2>
            <ul>
              <li><strong>Free Plan:</strong> Limited features and daily processing limits. No credit card required.</li>
              <li><strong>Pro Plan:</strong> $29/month with 500 credits, API access, and all enhancement features.</li>
              <li><strong>Business Plan:</strong> $99/month with unlimited credits, team accounts, and dedicated support.</li>
            </ul>
            <p>Subscriptions are billed monthly via Stripe. You may cancel at any time; access continues through the end of the billing period. We reserve the right to modify pricing with 30 days' notice.</p>

            <h2>7. Credit System</h2>
            <p>Processing jobs consume credits based on file type, size, and enhancement level. Unused credits do not roll over between billing periods. If you exceed your credit allocation, processing will be paused until the next billing cycle or until you upgrade.</p>

            <h2>8. API Usage</h2>
            <p>API access is subject to rate limits based on your subscription plan. You agree to:</p>
            <ul>
              <li>Keep your API keys confidential</li>
              <li>Not share API keys with unauthorized parties</li>
              <li>Comply with rate limits and usage quotas</li>
              <li>Use the API in accordance with our documentation</li>
            </ul>

            <h2>9. Data Processing & Privacy</h2>
            <p>Our handling of your data is governed by our Privacy Policy. Key commitments:</p>
            <ul>
              <li>Uploaded files are processed solely for enhancement purposes</li>
              <li>We never use your content to train our AI models</li>
              <li>Files are auto-deleted after the configured retention period</li>
              <li>We implement industry-standard security measures</li>
            </ul>

            <h2>10. Service Availability</h2>
            <p>We strive for high availability but do not guarantee uninterrupted service. We may perform maintenance, updates, or experience downtime. Business plan customers receive SLA guarantees as specified in their agreement.</p>

            <h2>11. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law, AIScaler shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service. Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim.</p>

            <h2>12. Disclaimer of Warranties</h2>
            <p>The Service is provided "as is" and "as available" without warranties of any kind, express or implied. We do not guarantee that AI enhancements will meet specific quality expectations for all content types.</p>

            <h2>13. Termination</h2>
            <p>We may suspend or terminate your account if you violate these Terms. You may delete your account at any time. Upon termination, your data will be deleted in accordance with our Privacy Policy.</p>

            <h2>14. Dispute Resolution</h2>
            <p>Any disputes arising from these Terms shall be resolved through binding arbitration, except where prohibited by law. You agree to attempt informal resolution before initiating formal proceedings.</p>

            <h2>15. Modifications</h2>
            <p>We may update these Terms from time to time. Material changes will be communicated via email or platform notice at least 30 days in advance. Continued use after changes take effect constitutes acceptance.</p>

            <h2>16. Contact</h2>
            <p>For questions about these Terms, please contact us at:</p>
            <ul>
              <li><strong>Email:</strong> info@aiscaler.app</li>
              <li><strong>Website:</strong> aiscaler.app</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
